import { logger } from '../../utils/logger'
import { Strings } from '../../constants/Strings'

type AnyPayload = Record<string, any>

export type NativeBridge = {
  postMessage(message: string): void
}

export type NativeErrorCode = 'JWT_DECODE_FAILED' | 'JWT_EXPIRED'

export type NativeResolveErrorCode =
  | 'SERVER_DOWN'
  | 'AUTH_FAILED'
  | 'INVALID_ACCESS'
  | 'PAGE_LOAD_FAILED'
  | 'MISSING_NATIVE_PERMISSION'

export interface NativeErrorPayload {
  code: NativeErrorCode
  message: string
  status: number
  category: string
  userMessage: string
  action: string
  extra?: Record<string, any>
}

export interface NativeResolveErrorPayload {
  source: 'Cobu'
  errorCode: NativeResolveErrorCode
  errorMessage: string
}

declare global {
  interface Window {
    ReactNativeWebView?: NativeBridge
    CobuNative?: NativeBridge
  }
}

type Sender = {
  target: 'reactNative' | 'cobuNativeBridge' | 'parentWindow' | 'selfWindow'
  send(message: AnyPayload): void
}

const getSenders = (): Sender[] => {
  if (typeof window === 'undefined') {
    return []
  }

  const senders: Sender[] = []

  // 1. React Native WebView
  if (window.ReactNativeWebView?.postMessage) {
    senders.push({
      target: 'reactNative',
      send: (message) => {
        window.ReactNativeWebView!.postMessage(JSON.stringify(message))
      },
    })
  }

  // 2. Generic native bridge for standalone Android/iOS apps
  if (window.CobuNative?.postMessage) {
    senders.push({
      target: 'cobuNativeBridge',
      send: (message) => {
        window.CobuNative!.postMessage(JSON.stringify(message))
      },
    })
  }

  // 3. Parent window (iframe host)
  if (
    window.parent &&
    window.parent !== window &&
    typeof window.parent.postMessage === 'function'
  ) {
    senders.push({
      target: 'parentWindow',
      send: (message) => {
        window.parent.postMessage(message, '*')
      },
    })
  }

  // 4. Local browser fallback
  if (!senders.length && typeof window.postMessage === 'function') {
    senders.push({
      target: 'selfWindow',
      send: (message) => {
        console.log('postToNative', message)
        window.postMessage(message, '*')
      },
    })
  }

  return senders
}

export const postToNative = (message: AnyPayload): boolean => {
  const senders = getSenders()
  if (!senders.length) {
    logger.warn('[NativeMessaging] No native or window bridge detected; skipping message.')
    return false
  }

  let delivered = false

  senders.forEach(({ target, send }) => {
    try {
      send(message)
      delivered = true
    } catch (error) {
      logger.error(`[NativeMessaging] Failed to post message via ${target}`, message, error)
    }
  })

  return delivered
}

const strings = Strings.get()

// Built per call, not once at module load: the proxy behind Strings.get() only
// forwards top-level key reads, so capturing the nested `sessionErrors.native`
// object would pin these to the language loaded when this module was first
// evaluated — which is always English, since the Spanish dictionary is awaited
// later in main.tsx.
const getNativeResolveErrorMessages = (): Record<NativeResolveErrorCode, string> => {
  const nativeSessionStrings = strings.sessionErrors.native

  return {
    SERVER_DOWN: nativeSessionStrings.serverDown,
    AUTH_FAILED: nativeSessionStrings.authFailed,
    INVALID_ACCESS: nativeSessionStrings.invalidAccess,
    PAGE_LOAD_FAILED: nativeSessionStrings.pageLoadFailed,
    MISSING_NATIVE_PERMISSION: nativeSessionStrings.missingNativePermission,
  }
}

const DEFAULT_NATIVE_RESOLVE_ERROR_CODE: NativeResolveErrorCode = 'SERVER_DOWN'
const DEFAULT_NATIVE_RESOLVE_ERROR_MESSAGE = 'Unexpected error while resolving user.'

export type NativePermission = 'calendar' | 'camera' | 'library'

export type NativePermissionMessage = {
  source: 'Cobu'
  type: 'CHECK_NATIVE_PERMISSION'
  permissions: NativePermission[]
  context?: string
}

export type NativePermissionRequestMessage = {
  source: 'Cobu'
  type: 'REQUEST_NATIVE_PERMISSION'
  permission: NativePermission
  context?: string
}

export type NativePermissionResultMessage = {
  source?: string
  type: 'NATIVE_PERMISSION_RESULT'
  permission: NativePermission
  granted: boolean
}

const postResolveErrorPayload = (payload: NativeResolveErrorPayload): boolean => {
  return postToNative(payload)
}

export const notifyNativeResolveError = (code: NativeResolveErrorCode, overrideMessage?: string): boolean => {
  const nativeResolveErrorMessages = getNativeResolveErrorMessages()
  const payloadCode = nativeResolveErrorMessages[code] ? code : DEFAULT_NATIVE_RESOLVE_ERROR_CODE
  const message = overrideMessage ?? nativeResolveErrorMessages[payloadCode] ?? DEFAULT_NATIVE_RESOLVE_ERROR_MESSAGE

  return postResolveErrorPayload({
    source: 'Cobu',
    errorCode: payloadCode,
    errorMessage: message,
  })
}

export const dispatchNativePermissionCheck = (
  permissions: NativePermission | NativePermission[],
  context?: string
): boolean => {
  const permissionArray = Array.isArray(permissions) ? permissions : [permissions]
  if (!permissionArray.length) {
    return false
  }

  const payload: NativePermissionMessage = {
    source: 'Cobu',
    type: 'CHECK_NATIVE_PERMISSION',
    permissions: permissionArray,
    context,
  }
  return postToNative(payload)
}

export const requestNativePermission = (
  permission: NativePermission,
  context?: string
): Promise<boolean> => {
  return new Promise((resolve) => {
    const payload: NativePermissionRequestMessage = {
      source: 'Cobu',
      type: 'REQUEST_NATIVE_PERMISSION',
      permission,
      context,
    }

    postToNative(payload)

    const handleResponse = (event: MessageEvent) => {
      const data = event.data as NativePermissionResultMessage
      if (data?.type === 'NATIVE_PERMISSION_RESULT' && data?.permission === permission) {
        window.removeEventListener('message', handleResponse)
        resolve(Boolean(data.granted))
      }
    }

    window.addEventListener('message', handleResponse)

    setTimeout(() => {
      window.removeEventListener('message', handleResponse)
      resolve(false)
    }, 60000)
  })
}
